import { Add, Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import { useListFetch } from '../../hooks/useListFetch';

const METHOD_OPTIONS = [
    { value: 'credit', label: 'Crédito' },
    { value: 'debit', label: 'Débito' },
    { value: 'pix', label: 'PIX' },
];

const emptyForm = () => ({
    paymentMethod: 'credit',
    feePercent: '',
    validFrom: '',
    validTo: '',
    notes: '',
});

function formatPercent(value) {
    if (value == null || value === '') {
        return '—';
    }
    const n = Number(value);
    if (Number.isNaN(n)) {
        return '—';
    }
    return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 3 })}%`;
}

function formatDateBR(value) {
    if (!value) {
        return '—';
    }
    const raw = String(value).slice(0, 10);
    const [year, month, day] = raw.split('-');
    if (!year || !month || !day) {
        return raw;
    }
    return `${day}/${month}/${year}`;
}

function PaymentFeesPage() {
    const navigate = useNavigate();
    const [fees, setFees] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [formError, setFormError] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const fetchFees = useCallback(() => {
        setIsLoading(true);
        return api.get('payment-fees')
            .then((response) => {
                setFees(Array.isArray(response.data?.fees) ? response.data.fees : []);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useListFetch(fetchFees, [fetchFees]);

    const updateForm = (patch) => {
        setForm((current) => ({ ...current, ...patch }));
    };

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm());
        setFormError('');
        setDialogOpen(true);
    };

    const openEdit = (fee) => {
        setEditing(fee);
        setForm({
            paymentMethod: fee.payment_method || 'credit',
            feePercent: fee.fee_percent != null ? String(fee.fee_percent) : '',
            validFrom: fee.valid_from ? String(fee.valid_from).slice(0, 10) : '',
            validTo: fee.valid_to ? String(fee.valid_to).slice(0, 10) : '',
            notes: fee.notes || '',
        });
        setFormError('');
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (isSaving) {
            return;
        }
        setDialogOpen(false);
    };

    const handleSave = (event) => {
        event.preventDefault();
        if (!form.paymentMethod) {
            setFormError('Informe o meio de pagamento.');
            return;
        }
        if (form.feePercent === '' || Number.isNaN(Number(form.feePercent))) {
            setFormError('Informe a taxa (%).');
            return;
        }
        if (!form.validFrom) {
            setFormError('Informe a data inicial da vigência.');
            return;
        }

        const payload = {
            payment_method: form.paymentMethod,
            fee_percent: Number(form.feePercent),
            valid_from: form.validFrom,
            notes: String(form.notes || '').trim(),
        };
        if (form.validTo) {
            payload.valid_to = form.validTo;
        } else {
            payload.valid_to = null;
        }

        setIsSaving(true);
        setFormError('');
        const request = editing
            ? api.put(`payment-fees/${editing.uuid || editing.id}`, payload)
            : api.post('payment-fees', payload);

        request
            .then(() => {
                setDialogOpen(false);
                return fetchFees();
            })
            .catch((error) => {
                const message = error?.response?.data?.error
                    || error?.response?.data?.message
                    || 'Não foi possível salvar a taxa.';
                setFormError(message);
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleDelete = (fee) => {
        const methodLabel = fee.payment_method_label || fee.payment_method || 'taxa';
        Swal.fire({
            title: 'Remover taxa?',
            text: `A regra de ${methodLabel} (${formatPercent(fee.fee_percent)}) será removida.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, remover',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            api.delete(`payment-fees/${fee.uuid || fee.id}`)
                .then(() => fetchFees())
                .catch((error) => {
                    const message = error?.response?.data?.error
                        || error?.response?.data?.message
                        || error?.message
                        || 'Não foi possível remover a taxa.';
                    Swal.fire('Erro', message, 'error');
                });
        });
    };

    if (isLoading && fees.length === 0) {
        return <Loading />;
    }

    if (didNotLoad && fees.length === 0) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/my-account')} to="/admin/my-account">
                    Minha Conta
                </Link>
                <Typography color="text.primary">Taxas da maquininha</Typography>
            </Breadcrumbs>
            <br />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Taxas da maquininha
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Cadastre o percentual por meio de pagamento (crédito, débito e PIX) com vigência por período.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
                    Nova taxa
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Meio</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Taxa</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Vigência</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Observações</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fees.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    Nenhuma taxa cadastrada ainda.
                                </TableCell>
                            </TableRow>
                        ) : (
                            fees.map((fee) => (
                                <TableRow key={fee.uuid || fee.id}>
                                    <TableCell>{fee.payment_method_label || fee.payment_method}</TableCell>
                                    <TableCell>{formatPercent(fee.fee_percent)}</TableCell>
                                    <TableCell>
                                        {formatDateBR(fee.valid_from)}
                                        {' — '}
                                        {fee.valid_to ? formatDateBR(fee.valid_to) : 'em aberto'}
                                    </TableCell>
                                    <TableCell>{fee.notes || '—'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton color="primary" onClick={() => openEdit(fee)} aria-label="editar">
                                            <Edit />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(fee)} aria-label="remover">
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <form onSubmit={handleSave}>
                    <DialogTitle>{editing ? 'Editar taxa' : 'Nova taxa'}</DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={Boolean(formError)}>
                                    <InputLabel id="payment-fee-method-label">Meio de pagamento</InputLabel>
                                    <Select
                                        labelId="payment-fee-method-label"
                                        label="Meio de pagamento"
                                        value={form.paymentMethod}
                                        onChange={(e) => updateForm({ paymentMethod: e.target.value })}
                                    >
                                        {METHOD_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Taxa (%)"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 0.001 }}
                                    value={form.feePercent}
                                    onChange={(e) => updateForm({ feePercent: e.target.value })}
                                    helperText="Ex.: 3,55 para crédito"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Vigência de"
                                    fullWidth
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={form.validFrom}
                                    onChange={(e) => updateForm({ validFrom: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="Vigência até"
                                    fullWidth
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    value={form.validTo}
                                    onChange={(e) => updateForm({ validTo: e.target.value })}
                                    helperText="Deixe em branco para vigência em aberto"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Observações"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={form.notes}
                                    onChange={(e) => updateForm({ notes: e.target.value })}
                                    helperText="Operadora, pacote de taxas, etc."
                                    inputProps={{ maxLength: 500 }}
                                />
                            </Grid>
                            {formError ? (
                                <Grid item xs={12}>
                                    <FormHelperText error>{formError}</FormHelperText>
                                </Grid>
                            ) : null}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog} disabled={isSaving}>Cancelar</Button>
                        <Button type="submit" variant="contained" disabled={isSaving} startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}>
                            {isSaving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </div>
    );
}

export default PaymentFeesPage;
